import { CommonModule } from '@angular/common';
import {
  Component,
  output,
  computed,
  ChangeDetectionStrategy,
  input,
  inject,
} from '@angular/core';
import { CtaButtonComponent } from '../../../../../shared/components/ui/cta-button/cta-button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { CourseInfoCardComponent } from './components/course-info-card/course-info-card.component';
import { CourseDescriptionCardComponent } from './components/course-description-card/course-description-card.component';
import { LessonsListComponent } from './components/lessons-list/lessons-list.component';
import { COURSE_CREATOR_BRIDGE } from '../bridges/course-creator-bridge.token';

/**
 * Main component for course publishing step
 * Follows Single Responsibility Principle - orchestrates sub-components
 * Follows Open/Closed Principle - can be extended with new sub-components without modification
 */
@Component({
  selector: 'app-course-publish',
  standalone: true,
  imports: [
    CommonModule,
    CtaButtonComponent,
    TranslatePipe,
    CourseInfoCardComponent,
    CourseDescriptionCardComponent,
    LessonsListComponent,
  ],
  templateUrl: './course-publish.component.html',
  styleUrl: './course-publish.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoursePublishComponent {
  #bridge = inject(COURSE_CREATOR_BRIDGE);
  stepCompleted = output<void>();
  selectedCourse = input<ChallengeGen.Course | undefined>(undefined);
  /** Admin catalog: hide school-only actions (e.g. generate audio) in lesson rows. */
  layoutVariant = input<'school' | 'admin'>('school');

  tasks = computed(() => this.selectedCourse()?.tasks || []);
  isPublishing = computed(() => this.#bridge.isPublishPending());

  async onComplete(): Promise<void> {
    const course = this.selectedCourse();
    if (!course?.id) return;
    try {
      await this.#bridge.publishCourse(course.id);
      this.stepCompleted.emit();
    } catch {
      // Error handled by mutation; user may retry
    }
  }
}
